import {
  IntranetContentKind,
  type IntranetHighlightSpan,
  type PinIntranetContentRequest,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ApiRequestError } from '@/lib/api';
import { IntranetHighlightDialog } from '@/products/intranet/components/intranet-highlight-dialog';
import { useSetIntranetPinned } from '@/products/intranet/features/intranet/use-intranet';

export function IntranetHighlightControls({
  slug,
  kind,
  pinned,
  highlightSpan,
  highlightUntil,
  persist,
  onFormChange,
}: {
  slug?: string;
  kind: string;
  pinned: boolean;
  highlightSpan: IntranetHighlightSpan | null;
  highlightUntil: string | null;
  persist: 'api' | 'form';
  onFormChange?: (span: IntranetHighlightSpan | null, until: string | null) => void;
}) {
  const setPinned = useSetIntranetPinned();
  const [open, setOpen] = useState(false);
  if (kind !== IntranetContentKind.NEWS) {
    return null;
  }

  async function save(input: PinIntranetContentRequest) {
    if (persist === 'form') {
      onFormChange?.(input.span, input.until ?? null);
      setOpen(false);
      return;
    }
    if (!slug) {
      return;
    }
    try {
      await setPinned.mutateAsync({ slug, pinned: true, ...input });
      onFormChange?.(input.span, input.until ?? null);
      toast.success(pinned ? 'Highlight-Zeit gespeichert.' : 'Highlight gesetzt.');
      setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Es können höchstens 6 Beiträge im Highlight stehen. Bitte zuerst einen lösen.',
      );
    }
  }

  async function clear() {
    if (persist === 'form') {
      onFormChange?.(null, null);
      return;
    }
    if (!slug) {
      return;
    }
    try {
      await setPinned.mutateAsync({ slug, pinned: false });
      onFormChange?.(null, null);
      toast.success('Highlight entfernt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Highlight entfernen fehlgeschlagen.',
      );
    }
  }

  return (
    <>
      {pinned || highlightSpan ? (
        <>
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
            Highlight-Zeit ändern
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => void clear()}>
            Aus Highlight nehmen
          </Button>
        </>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          Zur Highlight hinzufügen
        </Button>
      )}
      <IntranetHighlightDialog
        open={open}
        title={pinned || highlightSpan ? 'Highlight-Zeit ändern' : 'Zum Highlight hinzufügen'}
        initialSpan={highlightSpan}
        initialUntil={highlightUntil}
        pending={setPinned.isPending}
        onCancel={() => setOpen(false)}
        onSave={(input) => void save(input)}
      />
    </>
  );
}
